<script setup lang="ts">
import { ref, useTemplateRef, watch } from 'vue';

import CustomButton from '@/components/CustomButton.vue';
import { Sector } from '@/modules/rie/enums/sector';
import { useFormTemplateStore } from '@/modules/rie/stores/formTemplate';
import type { CreateFormTemplateRequest } from '@/modules/rie/types/requests/formTemplates/createFormTemplateRequest';
import { required } from '@/utils/validation';

const formTemplateStore = useFormTemplateStore();

const isDialogOpen = defineModel<boolean>();

const form = useTemplateRef('form');

const defaultRequest = { version: '1.0.0', isActive: true } as CreateFormTemplateRequest;
const request = ref<CreateFormTemplateRequest>({ ...defaultRequest });

const sectorOptions = Object.values(Sector).map((s) => ({ title: s, value: s }));

watch(isDialogOpen, (open) => {
    if (!open) form.value?.reset();
    request.value = { ...defaultRequest };
});

async function onConfirm() {
    const validation = await form.value?.validate();
    if (!validation?.valid) return;

    await formTemplateStore.create(request.value);
    onCancel();
}

function onCancel() {
    isDialogOpen.value = false;
    form.value?.reset();
}
</script>

<template>
    <v-dialog v-model="isDialogOpen" class="v-col-lg-5 v-col-md-7 v-col-sm-11" persistent>
        <v-row class="justify-center">
            <v-col lg="5" md="7" sm="11">
                <div class="dialog-content">
                    <v-card class="pa-5">
                        <v-card-title class="d-flex justify-space-between pl-0 mb-4">
                            <strong>Nieuw sjabloon</strong>
                            <v-icon icon="close" style="cursor: pointer" @click="onCancel" />
                        </v-card-title>

                        <v-form ref="form">
                            <v-text-field v-model="request.name" label="Naam" :rules="[required]" required density="compact" />

                            <v-autocomplete
                                v-model="request.sector"
                                :items="sectorOptions"
                                label="Sector"
                                :rules="[required]"
                                required
                                density="compact"
                            />

                            <v-text-field v-model="request.version" label="Versie" placeholder="bijv. 1.0.0" density="compact" />

                            <v-textarea v-model="request.description" label="Omschrijving" rows="2" auto-grow density="compact" />

                            <v-checkbox v-model="request.isActive" label="Actief" color="primary" hide-details density="compact" />
                        </v-form>

                        <v-card-actions class="pa-0 mt-4 justify-end ga-3">
                            <CustomButton text="Aanmaken" @click="onConfirm" />
                            <CustomButton color="red" text="Annuleren" @click="onCancel" />
                        </v-card-actions>
                    </v-card>
                </div>
            </v-col>
        </v-row>
    </v-dialog>
</template>
